import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Trash, Plus, Lock, CaretDown, CaretRight } from "@phosphor-icons/react";
import { FIELD_TYPE_LABELS, CHOICE_TYPES } from "@/components/CustomField";

const rid = (n) => Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) => "abcdefghijklmnopqrstuvwxyz0123456789"[b % 36]).join("");
export const newField = (type) => ({ key: `cf_${rid(8)}`, label: "New question", help: "", core: false, type, required: false, options: CHOICE_TYPES.includes(type) ? ["Option 1"] : [], show_if: null });
export const newSection = () => ({ id: `s_${rid(6)}`, title: "New section", fields: [] });
export const move = (list, i, d) => { const j = i + d; if (j < 0 || j >= list.length) return list; const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; return c; };

const IconBtn = ({ onClick, disabled, label, children, testId, danger }) => (
  <Button type="button" size="icon" variant="ghost" className={`h-8 w-8 ${danger ? "text-destructive" : ""}`} onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}>{children}</Button>
);

function OptionsEditor({ field, onChange }) {
  const opts = field.options;
  const set = (next) => onChange({ ...field, options: next });
  return (
    <div className="space-y-2" data-testid={`fb-options-${field.key}`}>
      <Label className="text-xs">Options</Label>
      {opts.map((o, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input value={o} maxLength={100} onChange={(e) => set(opts.map((x, j) => (j === i ? e.target.value : x)))} className="h-8" data-testid={`fb-option-${field.key}-${i}`} />
          <IconBtn label="Move option up" onClick={() => set(move(opts, i, -1))} disabled={i === 0}><ArrowUp size={14} /></IconBtn>
          <IconBtn label="Move option down" onClick={() => set(move(opts, i, 1))} disabled={i === opts.length - 1}><ArrowDown size={14} /></IconBtn>
          <IconBtn label="Remove option" danger onClick={() => set(opts.filter((_, j) => j !== i))} disabled={opts.length === 1} testId={`fb-option-remove-${field.key}-${i}`}><Trash size={14} /></IconBtn>
        </div>
      ))}
      {opts.length < 50 && <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => set([...opts, `Option ${opts.length + 1}`])} data-testid={`fb-option-add-${field.key}`}><Plus size={14} />Add option</Button>}
    </div>
  );
}

function ConditionEditor({ field, sources, onChange }) {
  const rule = field.show_if;
  const src = sources.find((s) => s.key === rule?.field);
  const setRule = (next) => onChange({ ...field, show_if: next });
  return (
    <div className="space-y-2 sm:col-span-2 rounded-md bg-muted/40 p-3" data-testid={`fb-condition-${field.key}`}>
      <Label className="text-xs">Show this question</Label>
      <Select value={rule?.field || "always"} onValueChange={(v) => setRule(v === "always" ? null : { field: v, values: [] })}>
        <SelectTrigger className="h-8" data-testid={`fb-condition-source-${field.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="always">Always</SelectItem>
          {sources.map((s) => <SelectItem key={s.key} value={s.key}>Only when “{s.label}” is…</SelectItem>)}
        </SelectContent>
      </Select>
      {rule && !src && <p className="text-xs text-destructive" data-testid={`fb-condition-invalid-${field.key}`}>The question this rule uses must come earlier in the form. Move it up or change the rule.</p>}
      {rule && src && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {src.options.map((o, i) => (
            <label key={o} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={rule.values.includes(o)} data-testid={`fb-condition-value-${field.key}-${i}`}
                onCheckedChange={(on) => { const kept = rule.values.filter((x) => src.options.includes(x) && x !== o); setRule({ ...rule, values: on ? [...kept, o] : kept }); }} />
              {o === "yes" ? "Yes" : o === "no" ? "No" : o}
            </label>
          ))}
          {!rule.values.some((x) => src.options.includes(x)) && <p className="w-full text-xs text-destructive" data-testid={`fb-condition-empty-${field.key}`}>Tick at least one answer.</p>}
        </div>
      )}
    </div>
  );
}

export function FieldRow({ field, index, count, sections, sectionIndex, onChange, onMove, onMoveTo, onRemove, sources }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid={`fb-field-${field.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        {!field.core && <IconBtn label={open ? "Collapse" : "Expand"} onClick={() => setOpen(!open)} testId={`fb-field-expand-${field.key}`}>{open ? <CaretDown size={14} /> : <CaretRight size={14} />}</IconBtn>}
        <Input value={field.label} maxLength={150} onChange={(e) => onChange({ ...field, label: e.target.value })} className="h-8 flex-1 min-w-[180px]" data-testid={`fb-field-label-${field.key}`} />
        <span className="text-xs rounded-full bg-muted px-2 py-0.5 text-muted-foreground inline-flex items-center gap-1">
          {field.core ? <><Lock size={11} />Core</> : FIELD_TYPE_LABELS[field.type]}
        </span>
        {field.show_if && <span className="text-[11px] rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 px-2 py-0.5" data-testid={`fb-field-conditional-${field.key}`}>Conditional</span>}
        {!field.core && <label className="flex items-center gap-1.5 text-xs"><Switch checked={field.required} onCheckedChange={(v) => onChange({ ...field, required: v })} data-testid={`fb-field-required-${field.key}`} />Required</label>}
        <Select value={String(sectionIndex)} onValueChange={(v) => onMoveTo(Number(v))}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`fb-field-section-${field.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>{sections.map((s, i) => <SelectItem key={s.id} value={String(i)}>{s.title || "Untitled"}</SelectItem>)}</SelectContent>
        </Select>
        <IconBtn label="Move up" onClick={() => onMove(-1)} disabled={index === 0} testId={`fb-field-up-${field.key}`}><ArrowUp size={14} /></IconBtn>
        <IconBtn label="Move down" onClick={() => onMove(1)} disabled={index === count - 1} testId={`fb-field-down-${field.key}`}><ArrowDown size={14} /></IconBtn>
        {!field.core && <IconBtn label="Remove field" danger onClick={onRemove} testId={`fb-field-remove-${field.key}`}><Trash size={14} /></IconBtn>}
      </div>
      {open && !field.core && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Help text</Label>
            <Input value={field.help} maxLength={300} onChange={(e) => onChange({ ...field, help: e.target.value })} className="h-8" data-testid={`fb-field-help-${field.key}`} /></div>
          {CHOICE_TYPES.includes(field.type) && <div className="sm:col-span-2"><OptionsEditor field={field} onChange={onChange} /></div>}
          <ConditionEditor field={field} sources={sources} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

export function SectionCard({ section, index, count, sections, onChange, onMove, onRemove, onFieldMoveTo, sourcesFor }) {
  const [adding, setAdding] = useState("");
  const fields = section.fields;
  const setFields = (next) => onChange({ ...section, fields: next });
  const hasCore = fields.some((f) => f.core);
  return (
    <Card className="p-4 border-border" data-testid={`fb-section-${section.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input value={section.title} maxLength={120} onChange={(e) => onChange({ ...section, title: e.target.value })} className="h-9 flex-1 min-w-[200px] font-semibold" data-testid={`fb-section-title-${section.id}`} />
        <IconBtn label="Move section up" onClick={() => onMove(-1)} disabled={index === 0} testId={`fb-section-up-${section.id}`}><ArrowUp size={15} /></IconBtn>
        <IconBtn label="Move section down" onClick={() => onMove(1)} disabled={index === count - 1} testId={`fb-section-down-${section.id}`}><ArrowDown size={15} /></IconBtn>
        <IconBtn label={hasCore ? "Move core fields out before removing this section" : "Remove section"} danger onClick={onRemove} disabled={hasCore} testId={`fb-section-remove-${section.id}`}><Trash size={15} /></IconBtn>
      </div>
      <div className="space-y-2">
        {fields.length === 0 && <p className="text-sm text-muted-foreground">No fields yet.</p>}
        {fields.map((f, i) => (
          <FieldRow key={f.key} field={f} index={i} count={fields.length} sections={sections} sectionIndex={index} sources={sourcesFor(f.key)}
            onChange={(nf) => setFields(fields.map((x, j) => (j === i ? nf : x)))}
            onMove={(d) => setFields(move(fields, i, d))} onMoveTo={(to) => onFieldMoveTo(i, to)}
            onRemove={() => setFields(fields.filter((_, j) => j !== i))} />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Select value={adding} onValueChange={(t) => { setFields([...fields, newField(t)]); setAdding(""); }}>
          <SelectTrigger className="h-9 w-52" data-testid={`fb-add-field-${section.id}`}><SelectValue placeholder="+ Add a field" /></SelectTrigger>
          <SelectContent>{Object.entries(FIELD_TYPE_LABELS).map(([t, l]) => <SelectItem key={t} value={t} data-testid={`fb-add-field-type-${t}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </Card>
  );
}
